// Sidebar.js
import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaChartBar,
  FaPlus,
  FaBullhorn,
  FaUserFriends,
  FaIdCard,
  FaCog,
  FaBuilding,
  FaShieldAlt,
  FaIndustry,
  FaRecycle,
  FaSearch,
} from "react-icons/fa";

const Sidebar = ({ selectedTeam, teams, clearTeamSelection }) => {
  const location = useLocation();
  console.log("Current location in sidebar:", location.pathname);

  const isActive = (path) => {
    // Handle the dashboard case specifically
    if (
      path === "/dashboard" ||
      path === "/" ||
      (path === "" && location.pathname.endsWith("/ai-sdr"))
    ) {
      return (
        location.pathname.endsWith("/dashboard") ||
        location.pathname.endsWith("/ai-sdr") ||
        location.pathname === "/ai-sdr/"
      );
    }
    // Handle other paths
    return location.pathname.includes(path);
  };

  // Helper function to preserve query parameters in navigation links
  const getNavPath = (path) => {
    // Convert internal path to full AI-SDR path and preserve query params
    const fullPath = path === "/" ? "/ai-sdr/" : `/ai-sdr${path}`;
    const finalPath = `${fullPath}${location.search}`;
    console.log(
      "getNavPath:",
      path,
      "->",
      finalPath,
      "search:",
      location.search
    );
    return finalPath;
  };

  const menuItems = [
    {
      path: "/",
      label: "Dashboard",
      icon: <FaChartBar />,
      description: "Overview and stats",
    },
    {
      path: "/campaign-builder",
      label: "Campaign Builder",
      icon: <FaPlus />,
      description: "Create and edit campaigns",
    },
    {
      path: "/campaigns",
      label: "Campaigns",
      icon: <FaBullhorn />,
      description: "Manage all campaigns",
    },
    {
      path: "/leads",
      label: "Leads",
      icon: <FaUserFriends />,
      description: "Track and manage sales leads",
    },
    {
      path: "/personas",
      label: "Personas",
      icon: <FaIdCard />,
      description: "Manage sender personas",
    },
    {
      path: "/companies",
      label: "Companies",
      icon: <FaBuilding />,
      description: "Manage company records and data",
    },
    {
      path: "/research",
      label: "Research Viewer",
      icon: <FaSearch />,
      description: "View enriched company research data",
    },
    {
      path: "/generators",
      label: "Generators",
      icon: <FaIndustry />,
      description: "Manage generator sites and find similar facilities",
    },
    {
      path: "/collectors",
      label: "Collectors",
      icon: <FaRecycle />,
      description: "Browse and search Reworld facilities",
    },
    {
      path: "/blacklist-checker",
      label: "Blacklist Checker",
      icon: <FaShieldAlt />,
      description: "Verify email addresses against blacklists",
    },
    {
      path: "/settings",
      label: "Settings",
      icon: <FaCog />,
      description: "Team and account settings",
    },
  ];

  return (
    <aside className="w-64 bg-white shadow-lg flex flex-col h-screen sticky top-0 border-r border-purple-100">
      {/* Logo and Team Info */}
      <div className="p-6 border-b border-purple-100 bg-gradient-to-r from-purple-50 to-violet-50">
        <Link to={getNavPath("/")}>
          <div className="flex items-center justify-center mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center mr-3">
              <FaChartBar className="text-white text-lg" />
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
              AI-SDR
            </h1>
          </div>
        </Link>

        {/* Team Information */}
        {selectedTeam && (
          <div className="bg-white rounded-lg p-4 border border-purple-200 shadow-sm">
            <div className="text-sm text-gray-600 font-medium">
              Active Team:
            </div>
            <div className="font-bold text-purple-700 text-lg mt-1">
              {selectedTeam.name}
            </div>
            {teams && teams.length > 1 && (
              <button
                onClick={clearTeamSelection}
                className="text-xs text-purple-600 hover:text-purple-800 mt-2 underline transition-colors"
              >
                Switch Team
              </button>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <ul className="space-y-2">
          {menuItems.map((item) => (
            <li key={item.path}>
              <Link
                to={getNavPath(item.path)}
                className={`
                  flex items-center px-4 py-3 rounded-lg transition-all duration-200 group
                  ${
                    isActive(item.path)
                      ? "bg-gradient-to-r from-purple-600 to-violet-600 text-white shadow-lg"
                      : "text-gray-700 hover:bg-purple-50 hover:text-purple-700"
                  }
                `}
              >
                <span
                  className={`mr-3 text-lg transition-transform duration-200 group-hover:scale-110 ${
                    isActive(item.path) ? "text-white" : "text-purple-600"
                  }`}
                >
                  {item.icon}
                </span>
                <div className="flex-1">
                  <div
                    className={`font-medium ${
                      isActive(item.path) ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {item.label}
                  </div>
                  <div
                    className={`text-xs mt-0.5 ${
                      isActive(item.path) ? "text-purple-100" : "text-gray-500"
                    }`}
                  >
                    {item.description}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-purple-100 bg-gradient-to-r from-purple-50 to-violet-50">
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <div className="bg-gradient-to-r from-purple-600 to-violet-600 rounded-full h-8 w-8 flex items-center justify-center text-white mr-2">
              <span className="font-bold text-sm">AI</span>
            </div>
            <span className="text-sm text-gray-600">Sales Development</span>
          </div>
          <div className="text-xs text-purple-600 font-medium">
            Version 2.0.0
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
